// Shared messenger button for the app header. Toggles `root._chatDrawerOpen`
// and, on open, bootstraps chat state via `@symbo.ls/collab-features` so the
// drawer renders immediately after. Unread badge is driven by the same
// package's `chat.unreadCount` helper; it lazy-loads once per app and
// caches the function on `root.__chatUnreadMod`.
import { activeHighlight } from './_activeHighlight.js'
import { headerOverlayPatch } from './headerOverlayState.js'

export const Messenger = {
  // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
  extends: ['IconButton', 'NavbarTooltipTrigger'],
  // Open-state highlight, on the component (works wherever it mounts).
  // class factory reads the signal directly so it stays reactive.
  ...activeHighlight,
  class: { isActive: (el, s) => !!s.root?._chatDrawerOpen },
  // INTERACT-1 item 4 (tickets/sonnet.md, WAVE-H interaction sweep,
  // 2026-08-14/15): live DOM inspection found this button — one of the two
  // top-left icon-only nav buttons (chat + notification bell, both in
  // AppHeader.js's HeaderLeft.HeaderActions cluster) — rendering with
  // NEITHER a `title` NOR an `aria-label` attribute. `title` stays flat
  // (unchanged — confirmed correct: `mcp__symbols-mcp__audit_component`
  // flags an `attr: { title }` wrapper as FA105, and smbls's own
  // attrs-in-props test suite (FA-L11, 2026-04-30) asserts flat `title`
  // promotes correctly on any tag, `button` included — an `attr` wrapper
  // would be a REGRESSION in canonical syntax, not a fix). Switched
  // `aria: { label }` (nested-object expansion path) to the bare
  // `'aria-label'` kebab key (direct-passthrough path,
  // `filterAttributesByTagName`'s "kebab-case aria-label passes through
  // unchanged" contract) — same value, a genuinely different code path,
  // and the exact convention AppHeader.js's own ClearSearchMorph/Me already
  // use in this package. If this is still unlabeled after this ships,
  // suspect a stale dist predating FA-L11 (rebuild, don't re-wrap in attr)
  // rather than re-adding the attr wrapper.
  //
  // Tooltip is "Direct messages" (not just "Messenger") so it reads as
  // clearly distinct from the `/chat` nav entry's "Team chat". Both used
  // to surface as "Chat"/"Messenger" with identical glyphs, which the
  // workspace-bug #2184 flagged as ambiguous.
  title: (el) => el.call('polyglot', 'shell.messenger.tooltip'),
  'aria-label': (el) => el.call('polyglot', 'shell.messenger.tooltip'),
  active: (el, s) => !!s.root?._chatDrawerOpen,

  onClick: async (e, el, s) => {
    const opening = !s.root._chatDrawerOpen
    // Bundle-fold: the MessengerOverlay (and the whole chat UI tree) lives in
    // the lazily-folded chat context — fold BEFORE flipping the flag so the
    // overlay's if-latch (app.js) can mount it. Closing needs no fold.
    if (opening) {
      const okFold = await el.call('ensureChatContext')
      if (okFold === false) return
      if (s.root.notificationsPanelOpen) el.call('closeDropdown')
    }
    // Open to the list only — clear any prior selection so MessengerChat stays
    // closed until the user picks a channel/DM.
    s.root.update(headerOverlayPatch('chat', opening, opening ? { activeChannelId: null } : {}))
    if (!opening) return
    // A BUNDLER resolves this specifier; a SERVED record does not — the
    // published page carries no import map for `@symbo-ls/workspace-chat/...`,
    // so the browser answers "Failed to resolve module specifier". Measured on
    // the served UI-kit catalog (2026-09-20). The drawer is ALREADY OPEN by
    // the line above, so this failure cannot be swallowed: a silent catch
    // leaves an empty panel and no explanation. It fills the drawer's own
    // error slot instead — the same `_chatDrawerError` key
    // chat/functions/collab/drawer.js writes on a failed load and renders in
    // its DrawerError child, so there is one failure surface, not two.
    try {
      const chat = await import('@symbo-ls/workspace-chat/functions/collab/index.js')
      if (chat.bootstrapChatState) {
        await chat.bootstrapChatState(s.root)
      } else {
        const { data } = await chat.listChannels()
        const channels = data || []
        const active = channels[0]?.id || null
        s.root.update({
          _chatDrawerChannels: channels,
          _chatDrawerChannelId: active,
          _chatDrawerLoading: false
        })
        if (active) {
          const res = await chat.listMessages({ channelId: active })
          // No `.reverse()` — this endpoint already answers oldest row first
          // (verified 2026-09-22; see chat/functions/collab/queries.js
          // `listMessages`). Nothing renders `_chatDrawerMessages` today, so
          // this is the same wrong assumption lying in wait for whoever wires
          // it up. `limit` dropped so the one default in `listMessages` owns
          // that number.
          s.root.update({ _chatDrawerMessages: res.data || [] })
        }
      }
    } catch (err) {
      console.error('[messenger] chat collab module failed to load:', err?.message || err)
      s.root.update({
        _chatDrawerLoading: false,
        _chatDrawerError: el.call('polyglot', 'shell.messenger.loadFailed')
      })
    }
  },

  // Lazy-load the unread helper once per app so the UnreadBadge factories
  // can compute synchronously from DOMQL.
  //
  // PURE ENHANCEMENT — it must fail SILENTLY. The same unresolvable specifier
  // as onClick above throws here on a served record, and it surfaced as
  // `[DOMQL] Lifecycle "onRender" error` on every mount of any header that
  // carries this button. Nothing depends on the helper: `UnreadBadge.if`
  // already answers false without it, so the badge is simply absent and the
  // header is otherwise whole. A console error for a missing badge is noise,
  // not information — the ONE place the user is owed a message is the click
  // path, which has it.
  onRender: async (el, s) => {
    if (s.root?.__chatUnreadMod) return
    try {
      const chat = await import('@symbo-ls/workspace-chat/functions/collab/index.js')
      const chatUtils = await import('@symbo-ls/workspace-chat/functions/chatUtils.js')
      s.root.update({
        __chatUnreadMod: {
          unreadCount: chat.unreadCount,
          // Workspace resolver so the badge counts ONLY the active
          // workspace's unreads (cross-workspace memberships are skipped).
          getActiveWorkspaceId: chatUtils.getActiveWorkspaceId
        }
      })
    } catch (_) {
      // No badge in this host. See the note above.
    }
  },

  // `comments` — a rectangular speech-bubble glyph (2026-06-08 per Nika:
  // "chat icon without circle"). The previous `sfMessageFill` was a filled
  // bubble enclosed in a circle; this drops the circle while still reading
  // as messaging, and stays distinct from the `/chat` nav's rounded `chat`
  // glyph. Registered in `@symbo-ls/brand/designSystem/icons.js`.
  //
  // `name` (not `icon`) — required, not stylistic. `IconText.Icon.if`
  // (smbls default-config components/Icon/index.js) gates child-creation on
  // `parent.icon || el.name || el.sfSymbols || parent.sfSymbols`; it never
  // reads the child's OWN `el.icon`. `SquareButton` used to ship a
  // `icon: 'smile'` placeholder that made `parent.icon` truthy for every
  // descendant, so this child rendered anyway despite using the wrong key —
  // until smbls `2e4ba032e` (2026-08-08) correctly removed that placeholder
  // (it was leaking a stray smiley onto unrelated text-only buttons), which
  // exposed this as a silent empty button (tickets/opus.md P1, 2026-08-09).
  // Same shape as the header's own working icons (HeaderTabs.js HomeBtn/
  // SearchBtn/ArrowBtn: `Icon: { name: '<glyph>', ... }`) — match that, not
  // a third shape.
  Icon: { name: 'comments', boxSize: 'A A' },

  UnreadBadge: {
    if: (el, s) => {
      const { unreadCount, getActiveWorkspaceId } = s.root?.__chatUnreadMod || {}
      if (!unreadCount) return false
      return (
        unreadCount({
          messagesByChannel: s.root?.messagesByChannel,
          channelMembers: s.root?.channelMembers,
          currentUserEmail: s.root?.currentUserEmail,
          activeWorkspaceId: getActiveWorkspaceId ? getActiveWorkspaceId(s.root) : null
        }) > 0
      )
    },
    position: 'absolute',
    top: '0',
    right: '0',
    minWidth: 'Z',
    height: 'Z',
    padding: '0 V',
    round: 'radiusPill',
    // Fixed alert-badge pairing, deliberately NOT theme-adaptive — an alert
    // reads red on both shells. But brand `red` (#EB6650) is tuned as a PLATE
    // colour, and white on it measures 3.21:1: fine for a dot (3:1 non-text
    // floor) and a fail for a COUNT, which is text. `redDeep` is the same
    // alert red deepened — white on #A83520 = 6.53:1. Measured across every
    // route by the aa-contrast sweep, 2026-08-09; 15 violation groups traced
    // to this one badge shape.
    background: 'redDeep',
    color: 'white',
    fontSize: 'W',
    fontWeight: '700',
    lineHeight: '1',
    align: 'center center',
    pointerEvents: 'none',
    text: (el, s) => {
      const { unreadCount, getActiveWorkspaceId } = s.root?.__chatUnreadMod || {}
      if (!unreadCount) return ''
      const n = unreadCount({
        messagesByChannel: s.root?.messagesByChannel,
        channelMembers: s.root?.channelMembers,
        currentUserEmail: s.root?.currentUserEmail,
        activeWorkspaceId: getActiveWorkspaceId ? getActiveWorkspaceId(s.root) : null
      })
      return n > 99 ? '99+' : String(n)
    }
  }
}
